// Chunked OMR upload queue.
//
// Uploads fail two ways when sending many/large sheets at once:
//   • too MANY files → GraphQL/request overhead
//   • too LARGE a request → Cloud Run rejects with "413 Content Too Large" (its
//     request limit is 32 MB; base64 inflates image bytes by ~33%).
// So we split by BOTH a max file COUNT and a max cumulative BYTE size, upload the
// batches SEQUENTIALLY (N+1 after N succeeds) with a small retry, and report
// progress via onProgress ("Uploading 30/120…").

export const BATCH_SIZE = 15;                 // max files per batch (secondary cap)
// Keep each request well under Cloud Run's 32 MB limit. base64 adds ~33%, plus JSON
// overhead, so target ~14 MB of raw image bytes per batch (~18–19 MB encoded).
const MAX_BATCH_BYTES = 14 * 1024 * 1024;

// Estimated raw byte size of a file item (File/Blob, base64 string, or {file,name}).
const sizeOf = (f) => {
  if (!f) return 0;
  if (typeof f === "object" && "file" in f) return sizeOf(f.file);
  if (typeof f === "object" && typeof f.size === "number") return f.size; // File/Blob
  if (typeof f === "string") return Math.ceil((f.length * 3) / 4);        // base64 → bytes
  return 0;
};

// Split into batches capped by BOTH count and cumulative bytes.
const sizedChunks = (files) => {
  const out = [];
  let cur = [];
  let curBytes = 0;
  for (const f of files) {
    const b = sizeOf(f);
    // start a new batch if adding this file would exceed either cap (but never
    // leave a batch empty — a single oversized file still goes in its own batch)
    if (cur.length && (cur.length >= BATCH_SIZE || curBytes + b > MAX_BATCH_BYTES)) {
      out.push(cur);
      cur = [];
      curBytes = 0;
    }
    cur.push(f);
    curBytes += b;
  }
  if (cur.length) out.push(cur);
  return out;
};

// Accepts either a File/Blob (read to a data-URL) or an already-encoded string
// (base64 / data-URL) which is passed through unchanged.
const fileToB64 = (f) =>
  typeof f === "string"
    ? Promise.resolve(f)
    : new Promise((res, rej) => {
        const r = new FileReader();
        r.onerror = rej;
        r.onload = () => res(r.result);
        r.readAsDataURL(f);
      });

// Upload one batch via the uploadFiles mutation. Returns on success, throws on error.
// Each file object carries the ORIGINAL filename (f.name for File objects) so the
// backend can store it against the sheet ("scan_045.jpg" etc.).
async function uploadBatch({ apiUrl, files, examPaperId, instituteId, subject, template, design }) {
  const fileObjs = await Promise.all(
    files.map(async (f) => {
      // f may be: a File/Blob, a base64 string, or { file, name }
      const src = f && typeof f === "object" && "file" in f ? f.file : f;
      const name = f && typeof f === "object" ? (f.name || "") : "";
      return { file: await fileToB64(src), name };
    }),
  );
  // subject + template are sent only for single-subject sheet batches; the backend seeds them
  // per row so the OMR reader uses the right template/subject. Normal uploads omit both.
  //
  // `design` is added to the mutation ONLY when it's "new" — a backend that doesn't yet know the
  // argument would reject the whole mutation ("Unknown argument design"). So an old-design upload
  // sends the exact original mutation the deployed backend already accepts, and a new-design
  // upload only works once the updated backend (schema+resolver) is deployed. This lets old
  // uploads keep working before the backend is deployed.
  const wantDesign = design === "new";
  const query = wantDesign
    ? "mutation($files:[FileInput!]!,$exam_paper_id:ID,$institute_id:ID,$subject:String,$template:String,$design:String){uploadFiles(files:$files,exam_paper_id:$exam_paper_id,institute_id:$institute_id,subject:$subject,template:$template,design:$design)}"
    : "mutation($files:[FileInput!]!,$exam_paper_id:ID,$institute_id:ID,$subject:String,$template:String){uploadFiles(files:$files,exam_paper_id:$exam_paper_id,institute_id:$institute_id,subject:$subject,template:$template)}";
  const variables = {
    files: fileObjs, exam_paper_id: String(examPaperId), institute_id: instituteId,
    subject: subject || null, template: template || null,
  };
  if (wantDesign) variables.design = "new";
  const body = JSON.stringify({ query, variables });
  const resp = await fetch(apiUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
  });
  const json = await resp.json();
  if (json.errors) throw new Error(json.errors.map((x) => x.message).join("; "));
  return json;
}

// Upload all `files` in sequential batches of BATCH_SIZE.
// opts: { apiUrl, examPaperId, instituteId, onProgress({done,total,batch,totalBatches,phase}), retries }
// Returns { uploaded, failedBatches:[{index, error}] }. failedBatches is empty on full success.
export async function uploadInBatches(files, opts) {
  const {
    apiUrl,
    examPaperId,
    instituteId,
    subject,
    template,
    design,
    onProgress = () => {},
    retries = 2,
  } = opts;

  const batches = sizedChunks(Array.from(files));
  const total = files.length;
  let done = 0;
  const failedBatches = [];

  for (let b = 0; b < batches.length; b++) {
    const batch = batches[b];
    let attempt = 0;
    // sequential: wait for this batch before the next
    // eslint-disable-next-line no-constant-condition
    while (true) {
      try {
        onProgress({ done, total, batch: b + 1, totalBatches: batches.length, phase: "uploading" });
        await uploadBatch({ apiUrl, files: batch, examPaperId, instituteId, subject, template, design });
        done += batch.length;
        onProgress({ done, total, batch: b + 1, totalBatches: batches.length, phase: "uploaded" });
        break;
      } catch (err) {
        attempt++;
        if (attempt > retries) {
          failedBatches.push({ index: b, error: err.message });
          onProgress({ done, total, batch: b + 1, totalBatches: batches.length, phase: "batch-failed", error: err.message });
          break; // move on to the next batch (skip failures, report at end)
        }
        onProgress({ done, total, batch: b + 1, totalBatches: batches.length, phase: "retrying", attempt });
        await new Promise((r) => setTimeout(r, 800 * attempt)); // backoff
      }
    }
  }

  return { uploaded: done, failedBatches };
}
